import { UploadOutlined } from "@ant-design/icons";
import { Form, Modal, Upload } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import i18n from "i18next";
import type { FC } from "react";
import { useState } from "react";

import { getAssignedDataGroup } from "../../core/router/auth";
import { ResourceName } from "../../core/router/config/ResourceName";
import { ActionBar } from "../ActionBar/ActionBar";
import { PrimaryButton, SecondaryButton } from "../Buttons/components/ActionButton";
import { TextInput } from "../Form/formComponents";
import { useUpdateTableDataMutation } from "../Table";
import { ImportErrorTitle, ImportResultContainer, ImportSuccessTitle, uploadPropsUtils } from "./csvModalButtonUtils";

export type XMLImportButtonProps = {
    id?: string;
    onSuccess?: () => void;
};

type XMLImportResult = {
    successCount?: number;
    failText?: string;
};

export const XMLImportButton: FC<XMLImportButtonProps> = ({ id, onSuccess }) => {
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [isImporting, setIsImporting] = useState(false);
    const [result, setResult] = useState<XMLImportResult | null>(null);

    const [importRequest] = useUpdateTableDataMutation();
    const dataGroup = getAssignedDataGroup();

    const handleOpen = () => {
        setIsModalVisible(true);
        setResult(null);
        setFileList([]);
        form.resetFields();
    };

    const handleClose = () => {
        if (isImporting) return;
        setIsModalVisible(false);
        setResult(null);
        setFileList([]);
        form.resetFields();
    };

    const handleImport = () => {
        form.validateFields().then((values) => {
            if (!fileList.length || !id) return;

            const file = fileList[0].originFileObj as File;
            setIsImporting(true);
            setResult(null);

            const formData = new FormData();
            formData.append("importFile", file);
            formData.append("suffix", values.suffix.trim());

            importRequest({
                url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.APPLICATION_PROFILE_LISTS}/${id}/xml-import`,
                method: "POST",
                body: formData,
            })
                .unwrap()
                .then((res) => {
                    setResult(res as unknown as XMLImportResult);
                    onSuccess?.();
                })
                .catch((error) => {
                    setResult({ failText: error?.data?.message || i18n.t("xmlImport.modal.genericError") });
                })
                .finally(() => setIsImporting(false));
        });
    };

    const uploadProps = {
        ...uploadPropsUtils(fileList, setFileList),
        accept: ".xml",
    };

    return (
        <>
            <SecondaryButton name="xml_import_button" onClick={handleOpen} icon={<UploadOutlined />}>
                {i18n.t("button.xmlImport")}
            </SecondaryButton>

            <Modal
                title={i18n.t("xmlImport.modal.title")}
                open={isModalVisible}
                onCancel={handleClose}
                footer={[
                    <SecondaryButton key="close" onClick={handleClose} disabled={isImporting}>
                        {i18n.t("common.close")}
                    </SecondaryButton>,
                    result?.successCount ? null : (
                        <PrimaryButton key="import" onClick={handleImport} disabled={fileList.length === 0 || isImporting}>
                            {i18n.t("button.import")}
                        </PrimaryButton>
                    ),
                ]}
            >
                <Upload {...uploadProps}>
                    <SecondaryButton icon={<UploadOutlined />}>{i18n.t("csvImport.modal.addFile")}</SecondaryButton>
                </Upload>

                <Form form={form}>
                    <ActionBar className={"column"} style={{ marginTop: 16 }}>
                        <Form.Item
                            name="suffix"
                            rules={[{ required: true, message: i18n.t("xmlImport.modal.suffixRequired") }]}
                            label={i18n.t("xmlImport.modal.suffix")}
                        >
                            <TextInput name={"suffix"} disabled={isImporting} placeholder={i18n.t("xmlImport.modal.suffixPlaceholder")} />
                        </Form.Item>
                    </ActionBar>
                </Form>

                {isImporting && (
                    <ImportResultContainer hasErrors={false}>
                        <p>{i18n.t("xmlImport.modal.inProgress")}</p>
                    </ImportResultContainer>
                )}

                {!isImporting && result && (
                    <ImportResultContainer hasErrors={!!result.failText}>
                        {result.successCount !== undefined && result.successCount !== 0 && (
                            <ImportSuccessTitle>{i18n.t("xmlImport.modal.success", { count: result.successCount })}</ImportSuccessTitle>
                        )}
                        {result.failText && <ImportErrorTitle>{result.failText}</ImportErrorTitle>}
                    </ImportResultContainer>
                )}
            </Modal>
        </>
    );
};
